import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  getDashboardChartMetadata,
  getPanelDashboard,
} from '../services/indicators/fuelsIndicatorsMarket';
import type { IDashboardChartMetadata } from '../services/indicators/fuelsIndicatorsMarket/types';
import { hydrateChartMetadataCache } from './dashboardChartMetadataCache';
import { getFilterOptions as getEthanolFilterOptions } from '../services/indicators/fuelsIndicatorsEthanol';
import { getMaxDateFromDatabase as getEthanolMaxDate } from '../services/indicators/fuelsIndicatorsEthanol';
import { getFilterOptions as getDerivativesFilterOptions } from '../services/indicators/fuelsIndicatorsPetroleumDerivatives';
import { getMaxDateFromDatabase as getDerivativesMaxDate } from '../services/indicators/fuelsIndicatorsPetroleumDerivatives';
import { getMaxDateFromDatabase as getBiodieselMaxDate } from '../services/indicators/fuelsIndicatorsBiodiesel';
import { getMaxDateFromDatabase as getQuotesMaxDate } from '../services/quotes/distributors';
import { queryKeys } from '../queries/queryKeys';
import { STALE_TIME_METADATA_MS, STALE_TIME_PANEL_MS } from '../queries/staleTimes';

let dashboardIndicatorsPrefetchStarted = false;

export function resetDashboardIndicatorsPrefetchCache(): void {
  dashboardIndicatorsPrefetchStarted = false;
}

export { hydrateChartMetadataCache } from './dashboardChartMetadataCache';

function prefetchEthanolMetadata(
  queryClient: ReturnType<typeof useQueryClient>
): Promise<unknown>[] {
  return [
    queryClient.prefetchQuery({
      queryKey: queryKeys.indicators.ethanol.filterOptions,
      queryFn: getEthanolFilterOptions,
      staleTime: STALE_TIME_METADATA_MS,
    }),
    queryClient.prefetchQuery({
      queryKey: queryKeys.indicators.ethanol.maxDate,
      queryFn: getEthanolMaxDate,
      staleTime: STALE_TIME_METADATA_MS,
    }),
  ];
}

function prefetchDerivativesMetadata(
  queryClient: ReturnType<typeof useQueryClient>
): Promise<unknown>[] {
  return [
    queryClient.prefetchQuery({
      queryKey: queryKeys.indicators.derivatives.filterOptions,
      queryFn: getDerivativesFilterOptions,
      staleTime: STALE_TIME_METADATA_MS,
    }),
    queryClient.prefetchQuery({
      queryKey: queryKeys.indicators.derivatives.maxDate,
      queryFn: getDerivativesMaxDate,
      staleTime: STALE_TIME_METADATA_MS,
    }),
  ];
}

function prefetchBiodieselMetadata(
  queryClient: ReturnType<typeof useQueryClient>
): Promise<unknown>[] {
  return [
    queryClient.prefetchQuery({
      queryKey: queryKeys.indicators.biodiesel.maxDate,
      queryFn: getBiodieselMaxDate,
      staleTime: STALE_TIME_METADATA_MS,
    }),
  ];
}

function prefetchQuotesMetadata(
  queryClient: ReturnType<typeof useQueryClient>
): Promise<unknown>[] {
  return [
    queryClient.prefetchQuery({
      queryKey: queryKeys.indicators.quotes.maxDate,
      queryFn: getQuotesMaxDate,
      staleTime: STALE_TIME_METADATA_MS,
    }),
  ];
}

function prefetchAllIndividualChartMetadata(
  queryClient: ReturnType<typeof useQueryClient>
): Promise<unknown> {
  return Promise.all([
    ...prefetchEthanolMetadata(queryClient),
    ...prefetchDerivativesMetadata(queryClient),
    ...prefetchBiodieselMetadata(queryClient),
    ...prefetchQuotesMetadata(queryClient),
  ]);
}

function prefetchMissingChartMetadata(
  queryClient: ReturnType<typeof useQueryClient>,
  metadata: IDashboardChartMetadata
): Promise<unknown> {
  const tasks: Promise<unknown>[] = [];

  if (!metadata.ethanol) {
    tasks.push(...prefetchEthanolMetadata(queryClient));
  }
  if (!metadata.derivatives) {
    tasks.push(...prefetchDerivativesMetadata(queryClient));
  }
  if (!metadata.biodiesel) {
    tasks.push(...prefetchBiodieselMetadata(queryClient));
  }
  if (!metadata.quotes) {
    tasks.push(...prefetchQuotesMetadata(queryClient));
  }

  return Promise.all(tasks);
}

async function prefetchDashboardChartMetadata(
  queryClient: ReturnType<typeof useQueryClient>
): Promise<void> {
  try {
    const [metadata] = await Promise.all([
      queryClient.fetchQuery({
        queryKey: queryKeys.indicators.chartMetadata,
        queryFn: getDashboardChartMetadata,
        staleTime: STALE_TIME_METADATA_MS,
      }),
      queryClient.prefetchQuery({
        queryKey: queryKeys.indicators.marketPanel.dashboard,
        queryFn: getPanelDashboard,
        staleTime: STALE_TIME_PANEL_MS,
      }),
    ]);

    hydrateChartMetadataCache(queryClient, metadata);
    await prefetchMissingChartMetadata(queryClient, metadata);
  } catch (error) {
    dashboardIndicatorsPrefetchStarted = false;
    console.error('Erro ao pré-carregar metadados do dashboard (batch):', error);
    await Promise.all([
      prefetchAllIndividualChartMetadata(queryClient),
      queryClient.prefetchQuery({
        queryKey: queryKeys.indicators.marketPanel.dashboard,
        queryFn: getPanelDashboard,
        staleTime: STALE_TIME_PANEL_MS,
      }),
    ]);
  }
}

/**
 * Pré-carrega metadados do dashboard (admin + summary).
 * Prioriza GET /dashboard-chart-metadata; em falha ou blocos ausentes, cai nos endpoints legados.
 */
export function useDashboardIndicatorsPrefetch(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (dashboardIndicatorsPrefetchStarted) {
      return;
    }
    dashboardIndicatorsPrefetchStarted = true;

    void prefetchDashboardChartMetadata(queryClient);
  }, [queryClient]);
}
